import { getNativeHeaders } from './getNativeHeaders'

type AppName = 'ios' | 'android'
const KEY = 'NativeAppName'

export const checkIsNativeApp = () => {
  return new RegExp(KEY).test(navigator.userAgent)
}

export const getNativeAppName = (): AppName => {
  const regex = new RegExp(`^(.*)(${KEY})/(ios|android)(.*)$`)
  return (navigator.userAgent).replace(regex, '$3') as AppName
}

export const checkIsIos = () => {
  return checkIsNativeApp() && getNativeAppName() === 'ios'
}

export const checkIsAndroid = () => {
  return checkIsNativeApp() && getNativeAppName() === 'android'
}

export const openAuth = (authUrl: string) => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.OpenAuth.postMessage(authUrl)
          break

        case 'android':
          window.Android.openAuth(authUrl)
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const authorize = (stringifyAuthData: string) => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.Authorize.postMessage(stringifyAuthData)
          break

        case 'android':
          window.Android.authorize(stringifyAuthData)
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const changeTheme = (theme: app.ThemeMode) => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.ChangeTheme.postMessage(theme)
          break

        case 'android':
          window.Android.changeTheme(theme)
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const registerPushNotifications = (forceChange: boolean) => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.RegisterPushNotifications.postMessage(getNativeHeaders(forceChange))
          break

        case 'android':
          window.Android.registerPushNotifications(getNativeHeaders(forceChange))
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const unregisterPushNotifications = (removeFromServer: boolean) => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.UnregisterPushNotifications.postMessage(
            removeFromServer ? getNativeHeaders() : null
          )
          break

        case 'android':
          if (removeFromServer) {
            window.Android.unregisterPushNotifications(getNativeHeaders())
          } else {
            window.Android.unregisterPushNotifications()
          }
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const checkPushNotificationStatus = () => {
  if (checkIsNativeApp()) {
    const appName = getNativeAppName()
    try {
      switch (appName) {
        case 'ios':
          window.webkit.messageHandlers.CheckPushNotifications.postMessage(null)
          break

        case 'android':
          window.Android.checkPushNotifications()
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
    }
  }
}

export const exportLogsInIos = () => {
  if (checkIsIos()) {
    console.log = function (log) {
      window.webkit.messageHandlers.Console.postMessage(JSON.stringify(log))
    }
    console.debug = console.log;
    console.info = console.log;
    console.warn = console.log;
    console.error = console.log;

    window.onerror = function (error, url, line) {
      console.log('ERROR: ' + error + ' URL:' + url + ' L:' + line);
    }
  }
}

/**
 * ---------------------------------------------------------------------------
 * Portfele (Apple Pay / Google Pay) w natywnej aplikacji
 * ---------------------------------------------------------------------------
 * DZIŚ WYŁĄCZONE i tak ma zostać do czasu prac nad natywną aplikacją:
 *  - Apple Pay JS NIE działa w WKWebView (trzeba PKPaymentRequest albo PayU Mobile SDK),
 *  - Google Pay w WebView też wymaga natywnego SDK.
 * `checkNativeWalletSupport` zwraca false dopóki natywna aplikacja nie zgłosi
 * obsługi przez `window.__NATIVE_APP__.wallets`, więc w appce metody portfelowe
 * po prostu nie pojawią się na liście (patrz src/payments/useAvailableMethods.ts).
 *
 * Poniższy mechanizm jest już gotowy do wpięcia: web wysyła żądanie przez
 * postMessage, natywna aplikacja otwiera swój arkusz i oddaje token wołając
 * `window.onNativeWalletResult({ success: true, token: '<surowy JSON tokenu>' })`.
 * KONTRAKT DO UZGODNIENIA Z ZESPOŁEM MOBILNYM - nazwy handlerów (`RequestWalletPayment`
 * / `requestWalletPayment`) i kształt odpowiedzi mogą się jeszcze zmienić.
 */

export interface NativeWalletRequest {
  wallet: 'applepay' | 'gpay'
  /** kwota jako string z dwoma miejscami po przecinku, np. '129.00' */
  amount: string
  currencyCode: string
  countryCode: string
  supportedNetworks: string[]
  /** nazwa pokazywana w arkuszu portfela */
  label: string
  gateway?: string
  gatewayMerchantId?: string
  merchantIdentifier?: string | null
  sandbox?: boolean
}

// mniej więcej tyle żyje arkusz płatności - po tym czasie nie ma już na co czekać
const NATIVE_WALLET_TIMEOUT = 3 * 60 * 1000

export const checkNativeWalletSupport = (wallet: 'applepay' | 'gpay') => {
  if (!checkIsNativeApp()) return false
  const wallets = window.__NATIVE_APP__?.wallets
  return Array.isArray(wallets) && wallets.includes(wallet)
}

/** Zwraca surowy JSON tokenu portfela albo odrzuca błędem z polem `code`. */
export const requestNativeWalletPayment = (request: NativeWalletRequest): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fail = (code: string) => reject(Object.assign(new Error(code), { code }))

    if (!checkNativeWalletSupport(request.wallet)) {
      fail('wallet_unavailable')
      return
    }

    let settled = false
    let timeout: ReturnType<typeof setTimeout>

    const finish = (result: NativeWalletResult | string) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      window.onNativeWalletResult = undefined

      const detail: NativeWalletResult = typeof result === 'string'
        ? (() => {
          try {
            return JSON.parse(result) as NativeWalletResult
          } catch (error) {
            return { success: false, error: 'wallet_failed' }
          }
        })()
        : result

      if (detail?.success && detail.token) resolve(detail.token)
      else fail(detail?.error === 'cancelled' ? 'wallet_cancelled' : (detail?.error || 'wallet_failed'))
    }

    timeout = setTimeout(() => finish({ success: false, error: 'wallet_timeout' }), NATIVE_WALLET_TIMEOUT)
    window.onNativeWalletResult = finish

    try {
      const message = JSON.stringify(request)
      switch (getNativeAppName()) {
        case 'ios':
          window.webkit.messageHandlers.RequestWalletPayment?.postMessage(message)
          break

        case 'android':
          window.Android.requestWalletPayment?.(message)
          break

        default: {
          throw new Error('Wrong app name')
        }
      }
    } catch (error) {
      console.error(error)
      finish({ success: false, error: 'wallet_unavailable' })
    }
  })
}

/**
 * ---------------------------------------------------------------------------
 * Natywne Sign in with Apple (iOS)
 * ---------------------------------------------------------------------------
 * Od iOS 27 strona appleid.apple.com w WKWebView nie przechodzi w systemowe
 * okno Face ID. Aplikacja iOS z handlerem `AppleSignIn` loguje natywnie:
 *  - web wysyła `{ nonce }` (SUROWY nonce - aplikacja podaje Apple jego SHA-256),
 *  - aplikacja odpowiada `window.onNativeAppleSignInResult({...})`
 *    (obiekt `NativeAppleSignInResult` albo JSON w stringu),
 *  - web wysyła identityToken do `POST v1/auth/apple/native` i dostaje magic token.
 * Bez handlera (Android, przeglądarki, stare wersje aplikacji iOS) - stary przepływ.
 */

// okno Apple + ewentualne logowanie hasłem Apple ID
const NATIVE_APPLE_SIGN_IN_TIMEOUT = 5 * 60 * 1000

export const checkNativeAppleSignInSupport = () => {
  return checkIsIos() && !!window.webkit?.messageHandlers?.AppleSignIn
}

const randomNonce = () => {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export interface NativeAppleSignInPayload {
  identityToken: string
  authorizationCode?: string
  firstName?: string
  lastName?: string
  nonce: string
}

export const requestNativeAppleSignIn = (): Promise<NativeAppleSignInPayload> => {
  return new Promise((resolve, reject) => {
    const fail = (code: string) => reject(Object.assign(new Error(code), { code }))

    if (!checkNativeAppleSignInSupport()) {
      fail('apple_unavailable')
      return
    }

    const nonce = randomNonce()
    let settled = false
    let timeout: ReturnType<typeof setTimeout>

    const finish = (result: NativeAppleSignInResult | string) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      window.onNativeAppleSignInResult = undefined

      const detail: NativeAppleSignInResult = typeof result === 'string'
        ? (() => {
          try {
            return JSON.parse(result) as NativeAppleSignInResult
          } catch (error) {
            return { success: false, error: 'apple_failed' }
          }
        })()
        : result

      if (detail?.success && detail.identityToken) {
        resolve({
          identityToken: detail.identityToken,
          authorizationCode: detail.authorizationCode,
          firstName: detail.firstName,
          lastName: detail.lastName,
          nonce,
        })
      } else {
        fail(detail?.error === 'cancelled'
          ? 'apple_cancelled'
          : detail?.error === 'unavailable' ? 'apple_unavailable' : (detail?.error || 'apple_failed'))
      }
    }

    timeout = setTimeout(() => finish({ success: false, error: 'apple_timeout' }), NATIVE_APPLE_SIGN_IN_TIMEOUT)
    window.onNativeAppleSignInResult = finish

    try {
      window.webkit.messageHandlers.AppleSignIn?.postMessage(JSON.stringify({ nonce }))
    } catch (error) {
      console.error(error)
      finish({ success: false, error: 'unavailable' })
    }
  })
}
